"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useNavigation } from "@/components/providers/NavigationProvider";
import { NavigationItem } from "@/types/navigation";
import { cn } from "@/utils/cn";

interface SidebarProps {
  navigationItems: NavigationItem[];
  className?: string;
}

export function Sidebar({ navigationItems, className }: SidebarProps) {
  const { isSidebarOpen, closeSidebar } = useNavigation();
  const pathname = usePathname();

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen) {
        closeSidebar();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isSidebarOpen, closeSidebar]);

  // Prevent body scroll when sidebar is open on mobile only
  useEffect(() => {
    const isMobile = window.innerWidth < 768;

    if (isSidebarOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth < 768) {
      closeSidebar();
    }
  };

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isSidebarOpen && (
        <div
          className={cn(
            "fixed inset-0 bg-black/50 z-40",
            "md:hidden",
            "transition-opacity duration-300"
          )}
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full z-50",
          "w-64",
          "transform transition-transform duration-300 ease-in-out",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
          className
        )}
        style={{
          background:
            "linear-gradient(180deg, #d8cdde 0%, #ebe5e9 50%, #f5f2f4 100%)",
        }}
      >
        {/* Logo Section */}
        <div
          className="h-16 flex items-center justify-between px-theme-md"
          style={{ borderBottom: "1px solid rgba(71, 50, 67, 0.2)" }}
        >
          <button
            onClick={closeSidebar}
            className={cn(
              "p-2 rounded-theme-md",
              "transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-offset-2"
            )}
            style={{
              color: "#473243",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(71, 50, 67, 0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            aria-label="Close sidebar"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <Link
            href="/"
            className="text-lg font-semibold flex-1 text-center cursor-pointer hover:opacity-80 transition-opacity"
            style={{ color: "#473243" }}
            onClick={handleLinkClick}
          >
            Granny Bot
          </Link>
          <div className="w-10" />
        </div>

        <nav className="flex flex-col h-full px-theme-sm py-theme-md">
          <ul className="space-y-1">
            {navigationItems.map((item) => {
              const isActive = pathname === item.href;

              return (
                <li key={item.id}>
                  <Link
                    href={item.href}
                    onClick={handleLinkClick}
                    className={cn(
                      "flex items-center gap-theme-sm",
                      "px-theme-md py-theme-sm",
                      "rounded-theme-md",
                      "transition-colors duration-200",
                      "focus:outline-none"
                    )}
                    style={{
                      color: "#473243",
                      backgroundColor: isActive
                        ? "rgba(71, 50, 67, 0.15)"
                        : "transparent",
                      fontWeight: isActive ? 600 : 400,
                      boxShadow: "none",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = "2px solid #473243";
                      e.currentTarget.style.outlineOffset = "2px";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.outline = "none";
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor =
                          "rgba(71, 50, 67, 0.08)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {item.icon && (
                      <span className="flex-shrink-0">{item.icon}</span>
                    )}
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}
